import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Calendar, ArrowRight, Globe, Truck, Shield, GraduationCap, Accessibility, Car, Sparkles, Cpu, Route, Radar, Clock, CalendarClock, Users, Flame, Briefcase } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { pageTemplates } from "../utils/pageTemplates";
import { COURSE_GROUPS, LANGUAGES, UCPLUS_LIVE_COURSE_IDS } from "../data/courseGroups";
import { HOME_INTRO, HOME_FAQ } from "../data/homeContent";
import { LatestArticleBar } from "../components/viden/DbArticleList";
import { PRIMARY, EBLUE, CYAN, Overline, MagneticButton, Spotlight, Reveal, Counter } from "../components/shared/kursusUi";
import { API_TIMEOUT } from "../utils/crawler";

const getSection = (sections, type) => sections?.find(s => s.type === type);
const PAGE_BG = "#F4F6FB";

const SEGMENTS = [
  { id: 'transport', label: 'Transport', shortLabel: 'Transport', icon: Truck },
  { id: 'it-ai', label: 'IT & Kunstig Intelligens', shortLabel: 'IT & AI', icon: Sparkles },
  { id: 'dansk', label: 'Dansk (FVU)', shortLabel: 'Dansk', icon: GraduationCap },
  { id: 'hakl', label: 'HAKL', shortLabel: 'HAKL', icon: Briefcase },
];
const CATEGORY_ICONS = { vognmand: Truck, chauffør: GraduationCap, arbejdsmiljø: Shield, flextrafik: Accessibility, taxa: Car, 'kunstig-intelligens': Cpu };

const AI_FEATURES = [
  { icon: Radar, title: "AI-drevet kursusmatch", desc: "Beskriv dit mål, vores AI peger straks på den rette uddannelse og vej til job.", span: "lg:col-span-7" },
  { icon: Route, title: "Personligt læringsspor", desc: "Et forløb tilpasset dit niveau, sprog og tempo.", span: "lg:col-span-5" },
  { icon: Clock, title: "Live pladser & datoer", desc: "Realtidsopdatering fra kalenderen, se ledige hold nu.", span: "lg:col-span-5" },
  { icon: GraduationCap, title: "Digitale beviser", desc: "Dit bevis ligger klar til download, så snart forløbet er bestået.", span: "lg:col-span-7" },
];

const STATS = [
  { value: "10+", label: "Års erfaring" },
  { value: "50+", label: "Kursushold udbudt" },
  { value: "98%", label: "Tilfredshed" },
  { value: "100%", label: "Online tilmelding" },
];

// Mouse-reactive light mesh background
const MeshBG = () => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--gx", `${(e.clientX - r.left) / r.width * 2 - 1}`);
    el.style.setProperty("--gy", `${(e.clientY - r.top) / r.height * 2 - 1}`);
  };
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" aria-hidden onMouseMove={onMove}>
      <div className="fx-aurora-light"><div className="lb lb--blue" /><div className="lb lb--violet" /><div className="lb lb--cyan" /></div>
      <div className="fx-mesh-light" />
    </div>
  );
};

const Scanner = () => (<div className="fx-scan"><span /></div>);

/* `/` og `/kurser` renderer samme komponent. Uden forskelligt indhold konkurrerer de to
   stærkeste URL'er om de samme søgninger. `variant` styrer hero, overskrift og den
   nederste indholdssektion, så forsiden er brandhub og /kurser er kursuskatalog. */
const VARIANTS = {
  home: {
    overline: "// Votra Danmark · uddannelser til transportbranchen",
    headline: ["Bliv", "vognmand", "eller", "chauffør"],
    lead: "Votra uddanner vognmænd, taxachauffører og varebilschauffører i hele Danmark. Find det rette forløb, se priser og prøvedatoer, og tilmeld dig online uden login.",
  },
  catalog: {
    overline: "// Kursuskatalog · alle forløb, priser og datoer",
    headline: ["Find", "dit", "næste", "kursus"],
    lead: "Alle Votras forløb samlet ét sted: vognmand til lastbil og varebil, taxa, chaufføruddannelser, arbejdsmiljø og IT & AI. Hvert kursus viser pris, varighed, adgangskrav og kommende datoer.",
  },
};

/* Krydslinks fra forsiden, så den fungerer som hub og ikke en blind vej. */
const HOME_HUB = [
  { to: "/kurser", title: "Alle kurser", desc: "Kursuskataloget med pris, varighed og adgangskrav på hvert forløb.", icon: GraduationCap },
  { to: "/kalender", title: "Kursus- og prøvedatoer", desc: "Kommende hold og prøvedatoer med ledige pladser.", icon: Calendar },
  { to: "/lokationer", title: "Søborg og Ringsted", desc: "Se hvor undervisningen og prøverne foregår.", icon: Route },
  { to: "/viden", title: "Viden om transport", desc: "Guides til vognmandstilladelse, taxakort, BAB og Flextrafik.", icon: Radar },
  { to: "/fvu", title: "FVU Dansk trin 2", desc: "Dansk på det niveau, taxauddannelsen kræver, med AI-tutor.", icon: Sparkles },
  { to: "/business", title: "Votra Business", desc: "Drift, bogføring og økonomi for vognmænd, i selskabet Votra Business ApS.", icon: Briefcase },
];

const CoursesPage = ({ variant = "catalog" }) => {
  const V = VARIANTS[variant] || VARIANTS.catalog;
  const [searchQuery, setSearchQuery] = useState("");
  const [pageData, setPageData] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState({});
  const [hiddenCourses, setHiddenCourses] = useState([]);
  const [activeSegment, setActiveSegment] = useState('transport');
  const [searchFocused, setSearchFocused] = useState(false);
  const [aiMatch, setAiMatch] = useState(null);
  const [aiMatchLoading, setAiMatchLoading] = useState(false);
  const [aiMatchError, setAiMatchError] = useState("");
  const coursesRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  // Header-undermenuen linker til /?spor=<fane>: vælg fanen og rul ned til kurserne.
  const location = useLocation();
  useEffect(() => {
    const spor = new URLSearchParams(location.search).get("spor");
    if (!SEGMENTS.some((x) => x.id === spor)) return;
    setActiveSegment(spor);
    const t = setTimeout(() => coursesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(t);
  }, [location.search, location.key]);

  const fetchData = async () => {
    try {
      const contentRes = await axios.get(`${API}/pages/forside`, { timeout: API_TIMEOUT });
      setPageData(contentRes.data);
    } catch { setPageData(pageTemplates["forside"]); }

    try {
      const visRes = await axios.get(`${API}/course-visibility`, { timeout: API_TIMEOUT });
      setHiddenCourses(visRes.data?.hidden || []);
    } catch { setHiddenCourses([]); }

    COURSE_GROUPS.forEach(async (group) => {
      let info = { count: 0, nextDate: null, seatsLeft: null };
      try {
        if (UCPLUS_LIVE_COURSE_IDS.includes(group.slug)) {
          const res = await axios.get(`${API}/ucplus-hold`, { params: { course: group.slug }, timeout: API_TIMEOUT });
          const courses = res.data?.courses || [];
          info.count = res.data?.count || courses.length || 0;
          if (courses[0]?.startDate) info.nextDate = courses[0].startDate;
        } else {
          const res = await axios.get(`${API}/course-groups/${group.slug}/sessions`, { timeout: API_TIMEOUT });
          const list = res.data || [];
          info.count = list.length;
          if (list[0]) {
            info.nextDate = list[0].date;
            const left = (list[0].total_spots ?? 20) - (list[0].booked_spots ?? 0);
            info.seatsLeft = left >= 0 ? left : null;
          }
        }
      } catch { /* keep defaults */ }
      setUpcomingSessions(prev => ({ ...prev, [group.slug]: info }));
    });
  };

  // Formatér YYYY-MM-DD eller DD-MM-YYYY til kort dansk dato (fx "12. sep")
  const formatDanishDate = (raw) => {
    if (!raw) return "";
    const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
    let d, m;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) { const [, mo, da] = raw.split("-"); d = parseInt(da); m = parseInt(mo); }
    else if (/^\d{2}-\d{2}-\d{4}/.test(raw)) { const [da, mo] = raw.split("-"); d = parseInt(da); m = parseInt(mo); }
    else return raw;
    if (!d || !m) return raw;
    return `${d}. ${months[m - 1]}`;
  };

  const data = pageData || pageTemplates["forside"];
  const hero = getSection(data.sections, 'hero');
  const config = getSection(data.sections, 'config');

  const SEARCH_STOPWORDS = new Set(['blive', 'bliv', 'vil', 'jeg', 'til', 'som', 'med', 'for', 'har', 'kan', 'skal', 'mit', 'min', 'den', 'det', 'kursus', 'uddannelse', 'blive til', 'og']);
  const matchesSearch = (group) => {
    if (hiddenCourses.includes(group.slug)) return false;
    if (!searchQuery.trim()) return true;
    const haystack = [group.title, group.shortTitle, group.description, group.shortDescription]
      .filter(Boolean).join(' ').toLowerCase();
    const q = searchQuery.toLowerCase().trim();
    if (haystack.includes(q)) return true;
    const tokens = q.split(/[\s,/.]+/).filter(t => t.length >= 3 && !SEARCH_STOPWORDS.has(t));
    if (tokens.length === 0) return haystack.includes(q);
    return tokens.some(t => haystack.includes(t));
  };

  const FRONT_ORDER = ['vb-gu', 'taxa'];
  const groupsForSegment = (segment) => {
    const list = COURSE_GROUPS.filter(g => (g.segment || 'transport') === segment && matchesSearch(g));
    return list.sort((a, b) => {
      const av = FRONT_ORDER.indexOf(a.slug) === -1 ? FRONT_ORDER.length : FRONT_ORDER.indexOf(a.slug);
      const bv = FRONT_ORDER.indexOf(b.slug) === -1 ? FRONT_ORDER.length : FRONT_ORDER.indexOf(b.slug);
      return av - bv;
    });
  };

  const itNewCount = COURSE_GROUPS.filter(g => g.segment === 'it-ai' && g.isNew).length;

  const searchSuggestions = searchQuery.trim()
    ? COURSE_GROUPS.filter(g => matchesSearch(g)).slice(0, 6)
    : [];

  const scrollToCourses = () => {
    if (coursesRef.current) {
      coursesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const submitSearch = () => {
    if (!searchQuery.trim()) return;
    // switch to the segment that actually has matches (prefer current)
    const currentHas = groupsForSegment(activeSegment).length > 0;
    if (!currentHas) {
      const other = activeSegment === 'transport' ? 'it-ai' : 'transport';
      if (groupsForSegment(other).length > 0) setActiveSegment(other);
    }
    setSearchFocused(false);
    setTimeout(scrollToCourses, 60);
  };

  const goToCourse = (group) => {
    if ((group.segment || 'transport') !== activeSegment) setActiveSegment(group.segment || 'transport');
    setSearchFocused(false);
    navigate(`/kurser/${group.slug}`);
  };

  const runAiMatch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setSearchFocused(false);
    setAiMatchLoading(true); setAiMatch(null); setAiMatchError("");
    try {
      const courses = COURSE_GROUPS.filter(g => !hiddenCourses.includes(g.slug)).map(g => ({
        slug: g.slug, title: g.shortTitle || g.title,
        description: g.shortDescription || g.description || "",
        segment: g.segment || "transport", duration: g.duration || "",
      }));
      const r = await axios.post(`${API}/course-groups/ai-match`, { query: q, courses });
      setAiMatch(r.data);
    } catch (e) {
      setAiMatchError(e.response?.data?.detail || "AI-match fejlede, prøv igen om lidt");
    } finally { setAiMatchLoading(false); }
  };

  const openMatchedCourse = (slug) => {
    const g = COURSE_GROUPS.find(x => x.slug === slug);
    if (!g) return;
    if ((g.segment || 'transport') !== activeSegment) setActiveSegment(g.segment || 'transport');
    navigate(`/kurser/${slug}`);
  };

  const getLanguageBadge = (group) => {
    if (group.hasLanguageVariants) {
      return (<div className="flex items-center gap-1 text-xs text-slate-700"><Globe className="h-3 w-3" /><span>{Object.keys(LANGUAGES).length} sprog</span></div>);
    }
    if (group.hasTypeVariants) {
      const count = group.slug === 'bab' ? 4 : (group.types?.length || 0);
      return (<div className="flex items-center gap-1 text-xs text-slate-700"><span>{count} kurser</span></div>);
    }
    return null;
  };

  const renderCard = (group, idx) => {
    const Icon = CATEGORY_ICONS[group.category] || GraduationCap;
    const info = upcomingSessions[group.slug] || { count: 0, nextDate: null, seatsLeft: null };
    let seatsBadge = null;
    if (info.seatsLeft != null) {
      if (info.seatsLeft <= 0) {
        seatsBadge = (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
            <Users className="h-3.5 w-3.5" /> Venteliste, næste hold snart
          </span>
        );
      } else if (info.seatsLeft <= 5) {
        seatsBadge = (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200 animate-pulse">
            <Flame className="h-3.5 w-3.5" /> Kun {info.seatsLeft} {info.seatsLeft === 1 ? "plads" : "pladser"} tilbage
          </span>
        );
      } else {
        seatsBadge = (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
            <Users className="h-3.5 w-3.5" /> Ledige pladser
          </span>
        );
      }
    }
    return (
      <Spotlight
        as={Link} key={group.id} to={`/kurser/${group.slug}`}
        dataTestid={`course-card-${group.slug}`}
        data-event="course_card_click"
        aria-label={`Se ${group.shortTitle || group.title}`}
        className="votra-rise course-card fx-card-light fx-spot-light group relative rounded-2xl overflow-hidden cursor-pointer h-full flex flex-col no-underline"
        style={{ animationDelay: `${Math.min(idx, 5) * 80}ms` }}
      >
        {group.isNew && (
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white shadow-[0_6px_18px_rgba(124,58,237,0.5)]" style={{ background: `linear-gradient(120deg,${EBLUE},#7C3AED)` }} data-testid={`new-badge-${group.slug}`}>
            <Sparkles className="h-3 w-3" /> Nyt
          </div>
        )}
        {group.image && (
          <div className="h-52 overflow-hidden relative">
            <img src={group.image} alt={group.title}
              className={`w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08] ${group.segment === 'it-ai' ? 'object-top' : ''}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
            <div className="absolute top-3 left-3">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur-md border border-white/70">
                <Icon className="h-3.5 w-3.5" style={{ color: EBLUE }} />
                <span className="text-xs font-medium text-slate-800 capitalize font-tech tracking-wide">{group.category?.replace('-', ' ')}</span>
              </div>
            </div>
            {getLanguageBadge(group) && (
              <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur-md border border-white/70">{getLanguageBadge(group)}</div>
            )}
          </div>
        )}
        <div className="p-6 flex flex-col flex-1 relative z-10">
          <h3 className="font-display text-2xl font-medium mb-2 leading-snug text-slate-900">{group.shortTitle || group.title}</h3>
          <p className="font-geo text-slate-600 leading-relaxed mb-4 line-clamp-2">{group.shortDescription}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-slate-500 mb-2 font-tech">
            {group.duration && (
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{group.duration}</span>
            )}
            {info.nextDate ? (
              <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: EBLUE }}>
                <CalendarClock className="h-3.5 w-3.5" /> {group.selfStudy ? "Næste prøve" : "Næste hold"} {formatDanishDate(info.nextDate)}
              </span>
            ) : info.count > 0 && (
              <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: EBLUE }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: EBLUE, boxShadow: `0 0 8px ${EBLUE}` }} />
                {info.count} {group.selfStudy ? "prøvedatoer" : "hold"}
              </span>
            )}
          </div>
          {seatsBadge && (
            <div data-testid={`seats-badge-${group.slug}`}>{seatsBadge}</div>
          )}
          <div className="mt-auto pt-5 flex items-center gap-2 font-semibold" style={{ color: EBLUE }}>
            Se detaljer
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </div>
        </div>
      </Spotlight>
    );
  };

  const renderGrid = (segment) => {
    const groups = groupsForSegment(segment);
    if (groups.length === 0) {
      return (<div className="text-center py-20"><p className="text-slate-500 font-geo">
        {searchQuery ? 'Ingen kurser matcher din søgning i dette segment.' : segment === 'it-ai' ? 'Nye IT- & AI-kurser er på vej. Hold øje med denne side!' : 'Ingen kurser tilgængelige endnu.'}
      </p></div>);
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((g, i) => renderCard(g, i))}
      </div>
    );
  };

  let touchStartX = 0;
  const onTouchStart = (e) => { touchStartX = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) < 60) return;
    if (dx < 0) setActiveSegment('it-ai'); else setActiveSegment('transport');
  };

  const headlineWords = V.headline;

  const NeonToggle = () => (
    <div className="mb-12 flex sm:justify-center overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="inline-flex items-center gap-1 p-1.5 rounded-full bg-white border border-slate-200 shadow-[0_10px_30px_rgba(30,58,138,0.10)] shrink-0 mx-auto" role="tablist" data-testid="segment-toggle">
        {SEGMENTS.map((seg) => {
          const SegIcon = seg.icon; const active = activeSegment === seg.id;
          return (
            <button key={seg.id} role="tab" aria-selected={active} onClick={() => setActiveSegment(seg.id)}
              className="relative flex items-center gap-2 px-3 sm:px-7 py-2.5 rounded-full text-sm font-semibold font-tech tracking-wide transition-colors duration-300 shrink-0"
              data-testid={`segment-tab-${seg.id}`}>
              {active && (<span className="absolute inset-0 rounded-full"
                style={{ background: `linear-gradient(120deg,${EBLUE},#7C3AED)`, boxShadow: `0 8px 26px ${EBLUE}66` }} />)}
              <span className={`relative flex items-center gap-2 ${active ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                <SegIcon className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap sm:hidden">{seg.shortLabel}</span>
                <span className="whitespace-nowrap hidden sm:inline">{seg.label}</span>
                {seg.id === 'dansk' && (
                  <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full hidden sm:inline-block ${active ? 'bg-white/25 text-white' : 'text-white'}`}
                    style={active ? {} : { background: `linear-gradient(120deg,${EBLUE},#7C3AED)` }} data-testid="segment-new-flag">Nyt</span>
                )}
                {seg.id === 'hakl' && (
                  <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full hidden sm:inline-block ${active ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-700'}`} data-testid="segment-soon-flag">Kommer snart</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: PAGE_BG }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* ===== HERO, light, mesh grid, gradient kinetic headline, AI search ===== */}
      <section className="relative overflow-hidden" style={{ backgroundColor: PAGE_BG }}>
        <MeshBG />
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-24 md:py-32">
          <div className="max-w-4xl">
            <Reveal><Overline>{V.overline}</Overline></Reveal>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02] mb-6 text-slate-900 flex flex-wrap gap-x-4">
              {headlineWords.map((w, i) => {
                const isLast = i === headlineWords.length - 1;
                return (
                  <React.Fragment key={i}>
                    {/* CSS-animation, ikke framer: framer skriver `opacity: 0` som inline style,
                        og prerenderingen snapshotter H1'en, før animationen er kørt. */}
                    <span
                      className={`votra-rise ${isLast ? "fx-grad fx-grad-anim" : ""}`}
                      style={{ animationDelay: `${0.05 + i * 0.1}s` }}
                    >{w}</span>
                    {/* Mellemrummet skal stå i DOM'en, ellers læser crawlere og AI-modeller
                        H1'en som "Findditnæstekursus". Flex-layoutet tegner det ikke, så
                        gap-x-4 står stadig for den visuelle afstand. */}
                    {!isLast && " "}
                  </React.Fragment>
                );
              })}
            </h1>
            <p className="votra-hero-rise votra-hero-rise--3 font-geo text-lg md:text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl">
              {/* CMS-teksten hører til forsiden. På /kurser bruges katalogteksten, ellers
                  ville de to stærkeste URL'er have præcis samme hero. */}
              {(variant === "home" && hero?.description) || V.lead}
            </p>

            <div className="votra-hero-rise votra-hero-rise--4 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <div className="relative flex items-center rounded-full bg-white border border-slate-200 shadow-[0_10px_30px_rgba(30,58,138,0.10)] focus-within:border-transparent focus-within:ring-2 focus-within:ring-[#2E6BFF]">
                  <Sparkles className="absolute left-4 h-5 w-5" style={{ color: EBLUE }} />
                  <input type="text" placeholder={config?.searchPlaceholder || "Beskriv dit mål, fx 'blive taxachauffør'..."}
                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (searchQuery.trim().split(/\s+/).length >= 3) runAiMatch(); else submitSearch(); } }}
                    className="w-full pl-12 pr-[7.5rem] py-3.5 rounded-full bg-transparent text-base text-slate-900 placeholder:text-slate-400 font-tech focus:outline-none"
                    data-testid="course-search-input" />
                  <button type="button" onClick={runAiMatch} disabled={aiMatchLoading || searchQuery.trim().length < 2}
                    className="absolute right-1.5 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
                    style={{ background: `linear-gradient(120deg, ${EBLUE}, #7C3AED)` }} data-testid="ai-match-btn">
                    {aiMatchLoading ? <Radar className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    AI-match
                  </button>
                </div>

                {searchFocused && searchQuery.trim() && (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.6rem)] z-30 rounded-2xl bg-white border border-slate-200 shadow-[0_24px_60px_rgba(30,58,138,0.18)] overflow-hidden"
                    data-testid="search-suggestions">
                    {searchSuggestions.length > 0 ? (
                      <>
                        {searchSuggestions.map((g) => {
                          const Icon = CATEGORY_ICONS[g.category] || GraduationCap;
                          return (
                            <button key={g.slug} type="button"
                              onMouseDown={(e) => { e.preventDefault(); goToCourse(g); }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                              data-testid={`search-suggestion-${g.slug}`}>
                              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, rgba(46,107,255,0.12), rgba(124,58,237,0.12))" }}>
                                <Icon className="h-4 w-4" style={{ color: EBLUE }} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-geo font-semibold text-slate-900 truncate">{g.shortTitle || g.title}</span>
                                <span className="block font-geo text-sm text-slate-500 truncate">{g.shortDescription}</span>
                              </span>
                              <span className="font-tech text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full shrink-0" style={{ color: EBLUE, background: "rgba(46,107,255,0.08)" }}>
                                {(g.segment || 'transport') === 'it-ai' ? 'IT & AI' : 'Transport'}
                              </span>
                              <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
                            </button>
                          );
                        })}
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); submitSearch(); }}
                          className="w-full text-center px-4 py-3 font-tech text-xs uppercase tracking-[0.18em] font-semibold hover:bg-slate-50 transition-colors"
                          style={{ color: EBLUE }} data-testid="search-see-all">
                          Vis alle {searchSuggestions.length} resultater ↓
                        </button>
                      </>
                    ) : (
                      <div className="px-4 py-6 text-center font-geo text-sm text-slate-500" data-testid="search-no-results">
                        Ingen kurser matcher "{searchQuery}". Prøv fx "taxa", "vognmand" eller "chauffør".
                      </div>
                    )}
                  </div>
                )}
              </div>
              <MagneticButton to="/kalender"
                className="fx-heartbeat group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold whitespace-nowrap no-underline"
                style={{ background: `linear-gradient(120deg, ${EBLUE}, #7C3AED)` }} data-testid="view-calendar-btn">
                <Calendar className="h-5 w-5" />
                {config?.calendarButton || "Se kursuskalender"}
              </MagneticButton>
            </div>

            {(aiMatchLoading || aiMatch || aiMatchError) && (
              <div
                className="votra-rise mt-5 max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-[0_18px_50px_rgba(30,58,138,0.14)] p-4 sm:p-5"
                data-testid="ai-match-result">
                {aiMatchLoading && (
                  <div className="flex items-center gap-3 text-slate-600 font-geo">
                    <Radar className="h-5 w-5 animate-spin" style={{ color: EBLUE }} />
                    AI finder de kurser der passer bedst til dit mål…
                  </div>
                )}
                {!aiMatchLoading && aiMatchError && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-geo text-sm text-slate-600" data-testid="ai-match-error">{aiMatchError}</span>
                    <button onClick={() => { setAiMatch(null); setAiMatchError(""); }} className="text-slate-400 hover:text-slate-700 text-sm">Luk</button>
                  </div>
                )}
                {!aiMatchLoading && aiMatch && (
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="font-geo text-slate-700 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 shrink-0" style={{ color: "#A855F7" }} />
                        <span data-testid="ai-match-intro">{aiMatch.intro}</span>
                      </p>
                      <button onClick={() => setAiMatch(null)} className="text-slate-400 hover:text-slate-700 text-sm shrink-0" data-testid="ai-match-close">Luk</button>
                    </div>
                    <div className="space-y-2">
                      {aiMatch.matches.map((m) => {
                        const g = COURSE_GROUPS.find(x => x.slug === m.slug) || {};
                        const Icon = CATEGORY_ICONS[g.category] || GraduationCap;
                        return (
                          <button key={m.slug} type="button" onClick={() => openMatchedCourse(m.slug)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-[#2E6BFF]/40 hover:bg-slate-50 transition-colors text-left"
                            data-testid={`ai-match-course-${m.slug}`}>
                            <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, rgba(46,107,255,0.12), rgba(168,85,247,0.12))" }}>
                              <Icon className="h-5 w-5" style={{ color: EBLUE }} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-geo font-semibold text-slate-900">{g.shortTitle || g.title || m.slug}</span>
                              <span className="block font-geo text-sm text-slate-500">{m.reason}</span>
                            </span>
                            <span className="inline-flex items-center gap-1 font-tech text-xs uppercase tracking-[0.15em] font-semibold shrink-0" style={{ color: EBLUE }}>
                              Se kurset <ArrowRight className="h-4 w-4" />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="votra-rise mt-8 flex flex-wrap gap-x-8 gap-y-2 font-tech text-xs uppercase tracking-[0.2em] text-slate-400"
              style={{ animationDelay: "0.5s" }}>
              <span>Online tilmelding</span><span className="text-slate-300">/</span>
              <span>Høj beståelsesprocent</span><span className="text-slate-300">/</span>
              <span>Åbent 07:30-23:30</span><span className="text-slate-300">/</span>
              <span>Hele Danmark</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== COURSES GRID ===== */}
      <section ref={coursesRef} className="relative scroll-mt-[72px] sm:scroll-mt-[84px] lg:scroll-mt-[104px]" style={{ backgroundColor: PAGE_BG }}>
        <div className="container mx-auto px-4 md:px-6 py-20 md:py-24 flex-1">
          {/* Kataloget bygger på statisk data i courseGroups.js, så griddet renderes med det samme.
              Før ventede det på et API-kald, og crawlerne snapshottede "Indlæser kurser...". */}
          <Reveal className="text-center mb-4"><div className="flex justify-center"><Overline>Vælg dit spor</Overline></div></Reveal>
          <NeonToggle />
          <div className="overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <div>
                  <div className={activeSegment === 'transport' ? '' : 'hidden'} data-testid="segment-panel-transport">{renderGrid('transport')}</div>
                  <div className={activeSegment === 'it-ai' ? '' : 'hidden'} data-testid="segment-panel-it-ai">
                    {groupsForSegment('it-ai').length > 0 && renderGrid('it-ai')}
                    <div className="mt-8 rounded-3xl overflow-hidden border border-slate-200" style={{ background: 'linear-gradient(140deg,#0B1020,#0e1a3a)' }}>
                      <div className="p-8 md:p-10">
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-200"><Cpu className="h-3 w-3" /> IT & AI · på vej</span>
                        <h3 className="mt-4 text-white font-bold text-2xl md:text-3xl">Fremtidens digitale kompetencer til transportbranchen</h3>
                        <p className="mt-3 text-slate-300 max-w-2xl">Vi bygger kurser der gør dig og dine medarbejdere fortrolige med AI-værktøjer, digitale arbejdsgange og effektivisering i hverdagen, skræddersyet til vognmænd og chauffører.</p>
                        <div className="mt-6 grid sm:grid-cols-3 gap-3">
                          {[[Radar,'AI i hverdagen','Brug AI til dokumentation, planlægning og kundeservice'],[Cpu,'Digitale arbejdsgange','Fra papir til app, arbejd hurtigere og smartere'],[Route,'Data & optimering','Forstå dine tal og optimér din drift']].map(([Ic,t,d],k)=>(
                            <div key={k} className="rounded-2xl bg-white/[0.05] border border-white/10 p-5">
                              <Ic className="h-5 w-5 text-cyan-300 mb-2" />
                              <div className="text-white font-semibold">{t}</div>
                              <div className="text-sm text-slate-400 mt-1">{d}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-6"><Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white no-underline" style={{ background: `linear-gradient(120deg,${EBLUE},#7C3AED)` }} data-testid="ai-notify-btn">Hold mig opdateret <ArrowRight className="h-4 w-4" /></Link></div>
                      </div>
                    </div>
                  </div>
                  <div className={activeSegment === 'dansk' ? '' : 'hidden'} data-testid="segment-panel-dansk">
                    <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(79,70,229,0.15)]" style={{ background: 'linear-gradient(140deg,#0B1020,#1e1b4b)' }}>
                      <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12 items-center">
                        <div>
                          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
                            <Sparkles className="h-3 w-3" /> Nyt · FVU-Dansk trin 2
                          </span>
                          <h3 className="mt-5 text-white font-bold text-3xl md:text-4xl leading-tight">Dansk trin 2 med AI-tutor på 12 sprog</h3>
                          <p className="mt-4 text-slate-300">12 moduler, oplæsning på alt indhold, ordforklaring på dit modersmål og realistiske øveprøver. Øv når du vil, i dit eget tempo.</p>
                          <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/fvu/app?demo=1" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white no-underline" style={{ background: 'linear-gradient(120deg,#4F46E5,#06B6D4)' }} data-testid="dansk-demo-btn">Prøv gratis demo</Link>
                            <Link to="/fvu" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white border border-white/20 bg-white/[0.05] hover:bg-white/[0.1] no-underline" data-testid="dansk-more-btn">Se mere og priser</Link>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {[['12','moduler'],['60','timer'],['3','øveprøver'],['12','sprog']].map(([n,l]) => (
                            <div key={l} className="rounded-2xl bg-white/[0.05] border border-white/10 p-5 text-center">
                              <div className="text-3xl font-bold text-white">{n}</div>
                              <div className="text-xs text-slate-400 mt-1">{l}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={activeSegment === 'hakl' ? '' : 'hidden'} data-testid="segment-panel-hakl">
                    <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 shadow-[0_20px_60px_rgba(30,58,138,0.08)]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="hakl-soon-badge">Kommer snart</span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold" style={{ color: '#0B1020' }}>Handel, Administration, Kommunikation & Ledelse</h3>
                      <p className="mt-3 text-slate-600 max-w-3xl">Vi udvider snart med kurser inden for HAKL, målrettet vognmænd og transportvirksomheder. Vi udvælger de forløb der styrker din daglige drift, økonomi og ledelse, så du både kan køre og drive en stærk forretning.</p>
                      <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                          [Calendar,'Økonomi & regnskab','Bogføring, moms og økonomistyring for din vognmandsforretning'],
                          [Briefcase,'Administration','Kontor, koordinering og effektive administrative arbejdsgange'],
                          [Route,'Logistik & indkøb','Lager, disponering og indkøb i handels- og speditionsvirksomhed'],
                          [Users,'Ledelse','Den grundlæggende lederuddannelse for dig med medarbejdere'],
                          [Sparkles,'AI & effektivisering','Optimér arbejdsprocesser og service med kunstig intelligens'],
                          [Globe,'Salg & service','Kundeservice og B2B-handel der løfter din virksomhed'],
                        ].map(([Ic,t,d],k)=>(
                          <div key={k} className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid={`hakl-card-${k}`}>
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: '#0B1020' }}><Ic className="h-5 w-5 text-white" /></div>
                            <div className="font-semibold" style={{ color: '#0B1020' }}>{t}</div>
                            <div className="text-sm text-slate-500 mt-1">{d}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-8"><Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white no-underline" style={{ background: `linear-gradient(120deg,${EBLUE},#7C3AED)` }} data-testid="hakl-notify-btn">Hold mig opdateret <ArrowRight className="h-4 w-4" /></Link></div>
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </section>

      {/* ===== STAT BAND (count-up) ===== */}
      <section className="relative overflow-hidden border-y border-slate-200 bg-white">
        <Scanner />
        <div className="container mx-auto px-4 md:px-6 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
            {STATS.map((s, i) => (
              <Reveal i={i} key={i} className="px-6 text-center">
                <p className="font-display text-4xl md:text-5xl font-medium fx-grad">{<Counter value={s.value} />}</p>
                <p className="mt-2 font-tech text-[11px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AI CAPABILITIES BENTO ===== */}
      <section className="relative overflow-hidden" style={{ backgroundColor: PAGE_BG }}>
        <Scanner />
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-24 md:py-28">
          <Reveal className="max-w-2xl mb-14">
            <Overline>Bygget til fremtiden</Overline>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-slate-900">
              Et uddannelsescenter der <span className="fx-grad">tænker med dig</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {AI_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Spotlight as="div" key={i}
                  className={`votra-rise fx-card-light fx-spot-light ${f.span} rounded-3xl p-8 md:p-10 min-h-[210px] flex flex-col`}
                  style={{ animationDelay: `${i * 90}ms` }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg, rgba(46,107,255,0.12), rgba(124,58,237,0.12))" }}>
                    <Icon className="h-6 w-6" style={{ color: EBLUE }} />
                  </div>
                  <h3 className="font-display text-2xl font-medium text-slate-900 mb-2">{f.title}</h3>
                  <p className="font-geo text-slate-600 leading-relaxed max-w-md">{f.desc}</p>
                </Spotlight>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== KINETIC MARQUEE BAND ===== */}
      <section className="relative overflow-hidden border-y border-slate-200 bg-white py-10">
        <div className="fx-marquee">
          <div className="fx-marquee-track">
            {[0, 1].map((k) => (
              <div key={k} className="flex items-center gap-10 pr-10 text-6xl md:text-8xl fx-outline" style={{ WebkitTextStroke: "1px rgba(30,58,138,.16)" }}>
                <span>VOGNMAND</span><span style={{ WebkitTextStroke: `1px ${EBLUE}55` }}>·</span>
                <span>TAXA</span><span style={{ WebkitTextStroke: `1px ${EBLUE}55` }}>·</span>
                <span>CHAUFFØR</span><span style={{ WebkitTextStroke: `1px ${EBLUE}55` }}>·</span>
                <span>AI</span><span style={{ WebkitTextStroke: `1px ${EBLUE}55` }}>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEO + FAQ (kun forsiden) / KURSUSINDEKS (kun /kurser) ===== */}
      {variant === "home" ? (
        <>
          <LatestArticleBar company="danmark" />
          <section className="relative border-t border-slate-200 bg-white" data-testid="home-seo-content">
            <Scanner />
            <div className="container mx-auto px-4 md:px-6 py-24 max-w-4xl">
              <Reveal>
                <Overline>Om Votra</Overline>
                <h2 className="font-display text-3xl md:text-4xl font-medium mb-6 text-slate-900 leading-tight">{HOME_INTRO.heading}</h2>
              </Reveal>
              <div className="space-y-5 font-geo text-slate-600 leading-relaxed text-lg">
                {HOME_INTRO.paragraphs.map((p, i) => (<Reveal as="p" i={i} key={i}>{p}</Reveal>))}
              </div>
              <Reveal>
                <h2 className="font-display text-3xl md:text-4xl font-medium mt-16 mb-8 text-slate-900 leading-tight">Ofte stillede spørgsmål</h2>
              </Reveal>
              <div className="divide-y divide-slate-200 border-t border-slate-200">
                {HOME_FAQ.map((f, i) => (
                  <Reveal i={i} key={i} className="py-6" data-testid={`home-faq-${i}`}>
                    <h3 className="text-lg font-semibold mb-2 flex items-start gap-3 text-slate-900 font-geo">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: EBLUE, boxShadow: `0 0 8px ${EBLUE}` }} />
                      {f.q}
                    </h3>
                    <p className="font-geo text-slate-600 leading-relaxed pl-[18px]">{f.a}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Hub-links, så forsiden ikke er en blind vej for hverken bruger eller crawler */}
          <section className="relative border-t border-slate-200" style={{ backgroundColor: PAGE_BG }} data-testid="home-hub">
            <div className="container mx-auto px-4 md:px-6 py-20 md:py-24">
              <Reveal className="max-w-2xl mb-12">
                <Overline>Kom videre</Overline>
                <h2 className="font-display text-3xl md:text-4xl font-medium text-slate-900 leading-tight">
                  Datoer, lokationer og <span className="fx-grad">viden om branchen</span>
                </h2>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {HOME_HUB.map((h, i) => {
                  const Icon = h.icon;
                  return (
                    <Link key={h.to} to={h.to} data-testid={`home-hub-${i}`}
                      className="votra-rise fx-card-light fx-spot-light group rounded-2xl p-7 flex flex-col no-underline"
                      style={{ animationDelay: `${Math.min(i, 5) * 80}ms` }}>
                      <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: "linear-gradient(135deg, rgba(46,107,255,0.12), rgba(124,58,237,0.12))" }}>
                        <Icon className="h-5 w-5" style={{ color: EBLUE }} />
                      </div>
                      <h3 className="font-display text-xl font-medium text-slate-900 mb-2">{h.title}</h3>
                      <p className="font-geo text-sm text-slate-600 leading-relaxed flex-1">{h.desc}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 font-tech text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: EBLUE }}>
                        Se mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      ) : (
        /* Kursusindeks: alle forløb som rigtige links med beskrivende anchor text, så både
           Google og AI-systemer kan se hele kataloget uden at skulle klikke på en fane. */
        <section className="relative border-t border-slate-200 bg-white" data-testid="course-index">
          <Scanner />
          <div className="container mx-auto px-4 md:px-6 py-24">
            <Reveal className="max-w-3xl mb-12">
              <Overline>Kursusindeks</Overline>
              <h2 className="font-display text-3xl md:text-4xl font-medium mb-5 text-slate-900 leading-tight">
                Alle kurser hos <span className="fx-grad">Votra</span>
              </h2>
              <p className="font-geo text-slate-600 leading-relaxed text-lg">
                Forløbene er delt i to spor: transportuddannelserne, der fører til vognmandstilladelse,
                taxakort eller chaufførbevis, og IT- og AI-forløbene. Hvert kursus har sin egen side med
                pris, varighed, adgangskrav, prøveform og kommende datoer.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10">
              {[
                { id: "transport", heading: "Transportuddannelser" },
                { id: "it-ai", heading: "IT og kunstig intelligens" },
              ].map((seg) => {
                const groups = COURSE_GROUPS.filter(
                  (g) => (g.segment || "transport") === seg.id && !hiddenCourses.includes(g.slug)
                );
                if (groups.length === 0) return null;
                return (
                  <div key={seg.id}>
                    <h3 className="font-display text-2xl font-medium text-slate-900 mb-5">{seg.heading}</h3>
                    <ul className="divide-y divide-slate-200 border-t border-slate-200">
                      {groups.map((g) => (
                        <li key={g.slug} className="py-4">
                          <Link to={`/kurser/${g.slug}`} data-testid={`course-index-${g.slug}`}
                            className="group inline-flex items-start gap-2 font-geo font-semibold text-slate-900 hover:text-[#0055FF] transition-colors">
                            {g.shortTitle || g.title}
                            <ArrowRight className="h-4 w-4 mt-1 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: EBLUE }} />
                          </Link>
                          {g.shortDescription && (
                            <p className="font-geo text-sm text-slate-600 leading-relaxed mt-1">{g.shortDescription}</p>
                          )}
                          {g.duration && (
                            <p className="font-tech text-xs uppercase tracking-[0.15em] text-slate-400 mt-1.5">{g.duration}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <p className="font-geo text-slate-600 leading-relaxed mt-12 max-w-3xl">
              Skal du se, hvornår det næste hold eller den næste prøve ligger, findes datoerne i{" "}
              <Link to="/kalender" className="font-semibold hover:underline" style={{ color: EBLUE }}>kursuskalenderen</Link>.
              Undervisningen og prøverne foregår i{" "}
              <Link to="/lokationer" className="font-semibold hover:underline" style={{ color: EBLUE }}>Søborg og Ringsted</Link>,
              og du kan læse mere om kravene i{" "}
              <Link to="/viden" className="font-semibold hover:underline" style={{ color: EBLUE }}>vores vidensartikler</Link>.
            </p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CoursesPage;
